import { AuthService } from '@teko/auth';
import { CartService } from '@teko/cart';
import { CartService as CartServiceV2 } from '@teko/cart/v2';
import { DiscoveryService } from '@teko/discovery';
import { DiscoveryService as DiscoveryServiceV2 } from '@teko/discovery/v2';
import { LocationService } from '@teko/location';
import { ILoyaltyBFFService } from '@teko/loyalty';
import { NotificationService } from '@teko/notification';
import { IOrderService } from '@teko/order';
import { IPaymentBffService, IPaymentService } from '@teko/payment';
import { PinService } from '@teko/pin';
import { PrivacyPolicyService } from '@teko/privacy-policy';
import { StaffService } from '@teko/staff-bff';
import { UserService } from '@teko/user';
import { WarehouseService } from '@teko/warehouse';
import { IQuotationService } from 'services/quotation';
import type { TenantEnv } from 'types';
import { DiscoveryHelper, isServerless } from '../helpers';
import {
  LoyaltyBFFService,
  OrderService,
  PaymentBffServiceWrapper as PaymentBffService,
  PaymentServiceWrapper as PaymentService,
  QuotationService,
} from '../lazy-modules';

export class ServiceContainer {
  private _authService: AuthService | undefined;
  private _userService: UserService | undefined;
  private _discoveryService: DiscoveryService | undefined;
  private _discoveryPos365Service: DiscoveryService | undefined;
  private _cartService: CartService | undefined;
  private _cartServiceV2: CartServiceV2 | undefined;
  private _locationService: LocationService | undefined;
  private _locationServiceV2: LocationService | undefined;
  private _locationServiceV3: LocationService | undefined;
  private _orderService: IOrderService | undefined;
  private _notificationService: NotificationService | undefined;
  private _warehouseService: WarehouseService | undefined;
  private _paymentService: IPaymentService | undefined;
  private _paymentBffService: IPaymentBffService | undefined;
  private _loyaltyBFFService: ILoyaltyBFFService | undefined;
  private _publicQuotationOrderService: IQuotationService | undefined;
  private _disicoveryServiceV2: DiscoveryServiceV2 | undefined;
  private _discoveryHelper: DiscoveryHelper | undefined;
  private _privacyPolicyService: PrivacyPolicyService | undefined;
  private _staffService: StaffService | undefined;
  private _pinService: PinService | undefined;

  private tenantConfig: TenantEnv;
  constructor(tenantConfig: TenantEnv) {
    this.tenantConfig = tenantConfig;
  }

  get authService() {
    if (!this._authService) {
      const iamApiUrl = this.tenantConfig.getProcessEnv('IAM_API_URL');
      const iamClientId =
        this.tenantConfig.iamClientId ||
        this.tenantConfig.getProcessEnv('NEXT_PUBLIC_IAM_CLIENT_ID');
      const iamOauthDomain =
        this.tenantConfig.iamOauthDomain ||
        this.tenantConfig.getProcessEnv('NEXT_PUBLIC_IAM_OAUTH_DOMAIN');

      this._authService = new AuthService(
        iamApiUrl,
        iamClientId,
        iamOauthDomain,
      );
    }

    return this._authService;
  }

  get userService() {
    if (!this._userService) {
      this._userService = new UserService(
        this.tenantConfig.getProcessEnv('NEXT_PUBLIC_BASE_USER_SERVICE_URL'),
        undefined,
        this.tenantConfig.platformId,
      );
    }
    return this._userService;
  }

  get discoveryService() {
    if (!this._discoveryService) {
      const publicDiscoveryEndpoint =
        this.tenantConfig.getProcessEnv('BASE_DISCOVERY_URL');
      const internalDiscoveryEndpoint = this.tenantConfig.getProcessEnv(
        'INTERNAL_DISCOVERY_URL',
      );
      const useTerminalId =
        this.tenantConfig.getProcessEnv('EPIC_ECOM_284') ||
        !!this.tenantConfig.useTerminalId;

      // use internal discovery endpoint
      // for server side rendering
      this._discoveryService = new DiscoveryService(
        typeof window === 'undefined' && internalDiscoveryEndpoint
          ? internalDiscoveryEndpoint
          : publicDiscoveryEndpoint,
        useTerminalId ? this.tenantConfig.terminalId : undefined,
        !useTerminalId ? this.tenantConfig.terminal : undefined,
        this.tenantConfig.currentLanguage,
      );
    }
    return this._discoveryService;
  }

  get discoveryServiceV2() {
    if (!this._disicoveryServiceV2) {
      const publicDiscoveryEndpoint = this.tenantConfig.getProcessEnv(
        'BASE_DISCOVERY_V2_URL',
      );
      const internalDiscoveryEndpoint = this.tenantConfig.getProcessEnv(
        'INTERNAL_DISCOVERY_V2_URL',
      );

      // use internal discovery endpoint
      // for server side rendering
      this._disicoveryServiceV2 = new DiscoveryServiceV2(
        typeof window === 'undefined' && internalDiscoveryEndpoint
          ? internalDiscoveryEndpoint
          : publicDiscoveryEndpoint,
        this.tenantConfig.terminalId,
        this.tenantConfig.currentLanguage,
        this.tenantConfig.location,
      );
    }
    return this._disicoveryServiceV2;
  }

  get discoveryPos365Service() {
    if (!this._discoveryPos365Service) {
      const useTerminalId =
        this.tenantConfig.getProcessEnv('EPIC_ECOM_284') ||
        !!this.tenantConfig.useTerminalId;

      this._discoveryPos365Service = new DiscoveryService(
        this.tenantConfig.getProcessEnv('BASE_POS365_DISCOVERY_URL'),
        useTerminalId ? this.tenantConfig.terminalId : undefined,
        !useTerminalId ? this.tenantConfig.terminal : undefined,
        this.tenantConfig.currentLanguage,
      );
    }
    return this._discoveryPos365Service;
  }

  get cartService() {
    if (!this._cartService) {
      this._cartService = new CartService(
        this.tenantConfig.getProcessEnv('BASE_CART_API_URL'),
        undefined,
        this.tenantConfig.currentLanguage,
      );
    }
    return this._cartService;
  }

  get cartServiceV2() {
    if (!this._cartServiceV2) {
      const useTerminalId =
        this.tenantConfig.getProcessEnv('EPIC_ECOM_284') ||
        !!this.tenantConfig.useTerminalId;
      const endpoint = isServerless()
        ? this.tenantConfig.getProcessEnv('BASE_CART_API_URL')
        : this.tenantConfig.getProcessEnv('INTERNAL_CART_API_URL');
      this._cartServiceV2 = new CartServiceV2(endpoint, {
        language: this.tenantConfig.currentLanguage,
        location: this.tenantConfig.location,
        useComboV2: !!this.tenantConfig.epicOmni1286,
        useExclusionV2:
          !!this.tenantConfig.featureFlags?.epic_omni_1403 &&
          !!this.tenantConfig.epicOmni1403,
        ...(useTerminalId
          ? { terminalId: this.tenantConfig.terminalId }
          : { terminal: this.tenantConfig.terminal }),
      });
    }
    return this._cartServiceV2;
  }

  get locationService() {
    if (!this._locationService) {
      this._locationService = new LocationService(
        this.tenantConfig.getProcessEnv('BASE_LOCATION_URL'),
      );
    }
    return this._locationService;
  }

  get locationServiceV2() {
    if (!this._locationServiceV2) {
      this._locationServiceV2 = new LocationService(
        this.tenantConfig.getProcessEnv('BASE_LOCATION_V2_URL'),
      );
    }
    return this._locationServiceV2;
  }

  get locationServiceV3() {
    if (!this._locationServiceV3) {
      this._locationServiceV3 = new LocationService(
        this.tenantConfig.getProcessEnv('BASE_LOCATION_V3_URL'),
      );
    }
    return this._locationServiceV3;
  }

  get orderService(): IOrderService {
    if (!this._orderService) {
      this._orderService = new OrderService(
        this.tenantConfig.getProcessEnv('BASE_COV_URL'),
        this.tenantConfig.currentLanguage,
      );
    }
    return this._orderService;
  }

  get pinService(): PinService {
    if (!this._pinService) {
      this._pinService = new PinService(
        this.tenantConfig.getProcessEnv('IAM_API_URL'),
      );
    }

    return this._pinService;
  }

  get notificationService() {
    if (!this._notificationService) {
      this._notificationService = new NotificationService(
        this.tenantConfig.getProcessEnv('BASE_NOTIFICATIONS_API'),
      );
    }
    return this._notificationService;
  }

  get warehouseService() {
    if (!this._warehouseService) {
      this._warehouseService = new WarehouseService(
        this.tenantConfig.getProcessEnv('BASE_WAREHOUSE_CONSUMER_API_URL'),
      );
    }
    return this._warehouseService;
  }

  get paymentService(): IPaymentService {
    if (!this._paymentService) {
      this._paymentService = new PaymentService({
        baseUrl: this.tenantConfig.getProcessEnv('BASE_PAYMENT_API_URL'),
        secretKey:
          this.tenantConfig.paymentSecretKey ||
          this.tenantConfig.getProcessEnv('PAYMENT_SECRET_KEY'),
        paymentSettingUrl: this.tenantConfig.getProcessEnv(
          'BASE_MAIN_PAYMENT_API_URL',
        ),
      });
    }
    return this._paymentService;
  }

  get paymentBffService(): IPaymentBffService {
    if (!this._paymentBffService) {
      this._paymentBffService = new PaymentBffService(
        this.tenantConfig.getProcessEnv('BASE_PAYMENT_BFF_API_URL'),
        this.tenantConfig.currentLanguage,
      );
    }
    return this._paymentBffService;
  }

  get loyaltyBFFService(): ILoyaltyBFFService {
    if (!this._loyaltyBFFService) {
      this._loyaltyBFFService = new LoyaltyBFFService(
        this.tenantConfig.getProcessEnv('BASE_LOYALTY_BFF_API_URL'),
        this.tenantConfig.getProcessEnv('LOYALTY_CLIENT_ID'),
        this.tenantConfig.currentLanguage,
      );
    }
    return this._loyaltyBFFService;
  }

  get publicQuotataionOrderService(): IQuotationService {
    if (!this._publicQuotationOrderService) {
      this._publicQuotationOrderService = new QuotationService();
    }
    return this._publicQuotationOrderService;
  }

  get discoveryHelper(): DiscoveryHelper {
    if (!this._discoveryHelper) {
      this._discoveryHelper = new DiscoveryHelper(this.tenantConfig);
    }
    return this._discoveryHelper;
  }

  get privacyPolicyService(): PrivacyPolicyService {
    if (!this._privacyPolicyService) {
      this._privacyPolicyService = new PrivacyPolicyService(
        this.tenantConfig.getProcessEnv('BASE_PRIVACY_POLICY_API_URL'),
        this.tenantConfig.platformId,
        this.tenantConfig.currentLanguage,
      );
    }
    return this._privacyPolicyService;
  }

  get staffBFFService(): StaffService {
    if (!this._staffService) {
      this._staffService = new StaffService(
        this.tenantConfig.getProcessEnv('BASE_STAFF_BFF_API_URL'),
      );
    }
    return this._staffService;
  }
}
